/**
 * Bank I-24, mobile half: upload an APK or IPA and see the tracker SDKs and
 * sensitive permissions found in it (POST /v1/discovery/evidence/mobile-app).
 * The controller scans the app in memory with no network call and stores one
 * operator-upload finding per tracker and per permission; the binary is never
 * kept. Same card pattern as the schema DDL upload on this page.
 *
 * The limits the card states are the controller's (upload-guard.ts): 96 MB,
 * a 300 s read deadline, and after a 10 s grace an average of at least
 * 256 KB/s. 96 MB in 300 s is 96 x 1024 x 1024 x 8 / 300 = 2.68 Mbit/s.
 *
 * Coverage (moved here from the PR #298 panel; this is the one I-24 card):
 * a scan that is not complete (a packed or shielded build, a split, unread
 * or obfuscated code, any iOS app: its binaries are read for the class names
 * the signature list gives, but a statically linked SDK with no listed or no
 * proven name is not seen) is shown as NOT complete, with what was not read,
 * never as a clean pass. An iOS result is shown as not complete even when a
 * controller says otherwise (round 2 of the #315 reviews). The card also says what an upload closed
 * from earlier builds, or why it closed nothing.
 */
import { useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, ArrowRight, CheckCircle2, Loader2, Smartphone, Upload } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  MOBILE_APP_MAX_BYTES,
  MobileAppUploadError,
  uploadMobileApp,
  type BusinessUnit,
  type MobileAppScanResponse,
} from '@/lib/api';

export const MIN_LINK_NOTE =
  'A 96 MB app needs a link of about 2.7 Mbit/s or faster to finish within the 5 minute upload limit. ' +
  'The server also stops an upload that averages under 256 KB/s (about 2.1 Mbit/s) after its first 10 seconds.';

export function formatBytes(n: number): string {
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${n} bytes`;
}

export function formatWait(seconds: number): string {
  if (seconds < 90) return `${seconds} seconds`;
  return `${Math.ceil(seconds / 60)} minutes`;
}

/** The sentence the card shows for a refused upload. Exported for tests. */
export function mobileUploadErrorMessage(err: unknown): string {
  if (!(err instanceof MobileAppUploadError)) {
    return err instanceof Error ? err.message : 'Upload failed.';
  }
  const wait = err.retryAfterSeconds;
  switch (err.status) {
    case 0:
    case 408:
      return (
        'The upload was too slow and the server stopped it. ' + MIN_LINK_NOTE +
        ' Send the app again over a faster connection.'
      );
    case 402:
      return 'This workspace has no Discovery licence, so apps cannot be scanned. Ask your administrator.';
    case 403:
      return err.message;
    case 413:
      return `The app is larger than the ${formatBytes(MOBILE_APP_MAX_BYTES)} upload limit.`;
    case 429:
      return wait
        ? `${err.message} Try again in about ${formatWait(wait)}.`
        : `${err.message} Try again later.`;
    case 503:
      return `Another app scan is running on this server. Try again in about ${formatWait(wait ?? 30)}.`;
    default:
      return err.message;
  }
}

/**
 * A plain sentence for each coverage gap the server names
 * (mobile-trackers/scan.ts COVERAGE_GAPS). A server test reads this table
 * from this file, so a new gap without a sentence fails.
 */
export const GAP_TEXT: Record<string, string> = {
  manifest_unreadable: 'the compiled AndroidManifest.xml could not be read, so the declared code and the build version were not checked',
  split_apk: 'this is one part of a split install (a configuration or feature split), not the whole app; upload a universal APK',
  split_install: 'this is the base of a split install; code in the other splits is not in this file',
  no_code_read: 'no app code was read (the file holds no readable DEX code)',
  dex_unreadable: 'a code file could not be read as DEX',
  dex_sections_unread: 'a code file holds more than the section that was read (a DEX container), so part of its code was not checked',
  dex_outside_root: 'a code file lies outside the archive root (often a sign of a packer)',
  work_budget_exceeded: 'a code file needed more work than its size allows and was not read in full',
  component_code_missing:
    'code for declared components was not found: the app may be packed or shielded (its real code encrypted and loaded at run time), so an SDK in that code was not seen',
  no_components_declared:
    'the manifest declares no activity, service, receiver, provider or application class, so the scan could not check that the app\'s own code was read (a shielded app can hide them)',
  dex_outside_sequence:
    'a code file is outside the sequence Android loads (classes.dex, classes2.dex, classes3.dex and so on, with no gaps), so it was not counted as the app\'s code',
  embedded_code:
    'the app carries code or an archive beside its own (a .jar, .apk, .zip or .dex in assets, or a file that starts like one); that content was not read. Bundled zip, jar or animation (.lottie) resources also cause this: review those entries by hand',
  entry_unreadable:
    'an entry in the archive could not be read (an unusual compression method, encryption, or data that would not unpack within the scan\'s limits), so it may hold code that was not checked',
  runtime_code_not_read:
    'the app carries code for a runtime the scan does not read, a bundle or its engine library (for example a React Native or Hermes bundle or engine, JavaScript, HTML pages, Lua or WebAssembly in its assets, Flutter, Unity, Xamarin and .NET MAUI, Titanium, NativeScript or J2V8); a tracker in that code was not seen. Code the app downloads at run time cannot be seen by any upload scan',
  entries_not_probed: 'the archive has more entries than the scan checks for hidden code, so some were not checked',
  manifest_value_mismatch:
    'a manifest attribute carries a raw string that differs from the value Android reads, so the declared components cannot be trusted',
  duplicate_entries: 'the archive holds two entries with the same name (a manifest or code file), and only one of them was read',
  components_not_checked: 'the manifest declares more components than the scan checks, so some were not looked for in the code',
  components_over_cap: 'the manifest declares more components than the limit',
  code_obfuscated: 'many class names are one to three characters (they look obfuscated or repackaged), so an SDK may have been renamed',
  code_shrunk_r8:
    'the code was shrunk by R8 for release, which can rename or merge an SDK\'s classes whatever their names look like, so an SDK missing from the list may still be in the app',
  ios_static_sdks_not_read:
    'iOS SDKs linked statically into the app binary leave no framework bundle and are found only by the class names the signature list gives; some SDKs (Swift-only ones, or ones with no class name listed) are not seen, and the names have not been checked against real iOS builds',
  ios_binary_encrypted:
    'an app binary is encrypted (App Store FairPlay), so nothing in it could be read; upload the build from your own pipeline (ad hoc, enterprise or development)',
  ios_binary_unreadable: 'an app binary could not be read as a Mach-O file, so an SDK linked into it was not seen',
  ios_no_executable: 'the app bundle holds no readable main executable, so the code linked into the app was not read',
  ios_binaries_not_read: 'the app binaries are larger than the scan reads, so some were not read',
};

export const CLOSE_SKIPPED_TEXT: Record<string, string> = {
  partial: 'Findings from earlier builds were not closed, because this scan is not complete.',
  version_unknown: 'Findings from earlier builds were not closed: the build names no version code.',
  signer_unknown: 'Findings from earlier builds were not closed: the app names no signing certificate (APK signature scheme v2 or v3).',
  signer_unverified:
    'Findings from earlier builds were not closed: the APK signature does not verify against the file (signed by another key than its certificate names, or changed after signing).',
};

/**
 * Why an earlier finding was kept open (mobile-trackers/close.ts
 * KEEP_OPEN_REASONS). A server test reads this table from this file.
 */
export const KEPT_OPEN_TEXT: Record<string, string> = {
  no_build_facts: 'recorded before build facts were kept, so only a person can close it',
  record_overflow: 'recorded by more builds than the record holds, so only a person can close it',
  package_mismatch: 'recorded by another app (package) under this reference',
  recorded_by_other_signer: 'recorded by a build signed with another certificate (a debug, unsigned or other build)',
  recorded_version_unknown: 'recorded by a build that named no version code',
  recorded_version_extreme: 'recorded by a build at the largest plain version code; only a build with a larger versionCodeMajor is newer',
  older_build: 'recorded by a newer build than this one',
};

function humanise(id: string): string {
  return id.replaceAll('_', ' ');
}

export function MobileAppUploadCard({ businessUnits }: { businessUnits: ReadonlyArray<BusinessUnit> }) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [assetExternalRef, setAssetExternalRef] = useState('');
  const [uploadLabel, setUploadLabel] = useState('');
  const [businessUnitId, setBusinessUnitId] = useState('');
  const [sent, setSent] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MobileAppScanResponse | null>(null);

  function reset() {
    setFile(null);
    setAssetExternalRef('');
    setUploadLabel('');
    setBusinessUnitId('');
    setSent(null);
    setSubmitting(false);
    setError(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function onFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0] ?? null;
    setError(null);
    setFile(null);
    if (!picked) return;
    if (!/\.(apk|ipa)$/i.test(picked.name)) {
      setError('Choose an Android .apk or an iOS .ipa file.');
      return;
    }
    if (picked.size > MOBILE_APP_MAX_BYTES) {
      setError(
        `${picked.name} is ${formatBytes(picked.size)}, over the ${formatBytes(MOBILE_APP_MAX_BYTES)} upload limit. It was not sent.`,
      );
      return;
    }
    if (picked.size === 0) {
      setError(`${picked.name} is empty.`);
      return;
    }
    setFile(picked);
    if (!uploadLabel) setUploadLabel(picked.name);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file || !assetExternalRef.trim()) return;
    setSubmitting(true);
    setError(null);
    setSent(0);
    try {
      const res = await uploadMobileApp(
        file,
        {
          assetExternalRef: assetExternalRef.trim(),
          fileName: file.name,
          uploadLabel: uploadLabel.trim() || undefined,
          businessUnitId: businessUnitId || undefined,
        },
        (loaded) => setSent(loaded),
      );
      setResult(res);
    } catch (err) {
      setError(mobileUploadErrorMessage(err));
    } finally {
      setSubmitting(false);
      setSent(null);
    }
  }

  const pct = file && sent !== null ? Math.min(100, Math.round((sent / file.size) * 100)) : null;

  return (
    <GlassCard className="p-5 mb-6 border-primary/30" data-testid="mobile-app-upload-card">
      <CardHeader className="p-0 mb-4">
        <CardTitle className="flex items-center gap-2 text-base">
          <Smartphone className="h-4 w-4 text-primary" /> Scan a mobile app for trackers
        </CardTitle>
        <CardDescription className="text-xs">
          Upload an Android <code className="font-mono">.apk</code> or iOS <code className="font-mono">.ipa</code>.
          Discovery reads the app offline, matches it against its list of tracker SDKs and sensitive Android
          permissions, and records one finding for each. The app itself is not stored, only its SHA-256.
        </CardDescription>
      </CardHeader>

      {result ? (
        <MobileAppFindings result={result} onAnother={reset} />
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label htmlFor="mobile-asset-ref" className="text-xs font-medium block mb-1">
                App reference <span className="text-sev-high">*</span>
              </label>
              <input
                id="mobile-asset-ref"
                type="text"
                value={assetExternalRef}
                onChange={(e) => setAssetExternalRef(e.target.value)}
                placeholder="e.g. mobileapp://com.bank.retail"
                required
                maxLength={500}
                className="glass-lite rounded-md px-3 py-2 text-sm w-full outline-none focus:border-primary/40 border border-border/40 font-mono"
              />
            </div>
            <div>
              <label htmlFor="mobile-upload-label" className="text-xs font-medium block mb-1">
                Upload label <span className="text-muted-foreground">(optional)</span>
              </label>
              <input
                id="mobile-upload-label"
                type="text"
                value={uploadLabel}
                onChange={(e) => setUploadLabel(e.target.value)}
                maxLength={200}
                className="glass-lite rounded-md px-3 py-2 text-sm w-full outline-none focus:border-primary/40 border border-border/40"
              />
            </div>
          </div>

          {businessUnits.length > 0 && (
            <div>
              <label htmlFor="mobile-business-unit" className="text-xs font-medium block mb-1">
                Business unit <span className="text-muted-foreground">(who can see these findings)</span>
              </label>
              <select
                id="mobile-business-unit"
                value={businessUnitId}
                onChange={(e) => setBusinessUnitId(e.target.value)}
                className="glass-lite rounded-md px-3 py-2 text-sm w-full outline-none focus:border-primary/40 border border-border/40"
              >
                <option value="">Default - your own unit, or the whole workspace if you see every unit</option>
                {businessUnits.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label htmlFor="mobile-app-file" className="text-xs font-medium block mb-1">
              App file (.apk or .ipa, up to 96 MB) <span className="text-sev-high">*</span>
            </label>
            <input
              id="mobile-app-file"
              ref={fileInputRef}
              type="file"
              accept=".apk,.ipa"
              onChange={onFilePicked}
              className="text-xs"
            />
            {file && (
              <div className="text-xs mt-1" data-testid="mobile-app-file-size">
                {file.name} - {formatBytes(file.size)}
              </div>
            )}
            <div className="text-[10px] text-muted-foreground mt-1" data-testid="mobile-min-link">
              {MIN_LINK_NOTE} Each server scans one app at a time, and a workspace can upload 20 apps an hour.
            </div>
          </div>

          {pct !== null && (
            <div data-testid="mobile-upload-progress">
              <div className="h-1.5 rounded bg-border/40 overflow-hidden">
                <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
              </div>
              <div className="text-[10px] text-muted-foreground mt-1 tabular">
                {pct < 100 ? `Sending ${pct}% of ${formatBytes(file?.size ?? 0)}` : 'Sent. Scanning the app'}
              </div>
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="glass-lite border border-sev-high/40 rounded-md p-3 text-xs flex items-start gap-2"
            >
              <AlertTriangle className="h-4 w-4 text-sev-high shrink-0 mt-0.5" />
              <div>{error}</div>
            </div>
          )}

          <div className="flex items-center gap-2">
            <Button type="submit" size="sm" disabled={submitting || !file || !assetExternalRef.trim()}>
              {submitting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
              {submitting ? 'Uploading and scanning' : 'Scan app'}
            </Button>
            <Button type="button" size="sm" variant="glass" onClick={reset} disabled={submitting}>
              Reset
            </Button>
          </div>
        </form>
      )}
    </GlassCard>
  );
}

export function MobileAppFindings({ result, onAnother }: { result: MobileAppScanResponse; onAnother?: () => void }) {
  // A controller from before this change sends no coverage: that is shown as
  // not complete (nothing says the code was read), never as a clean pass.
  // An iOS scan is never complete (statically linked SDKs: GAP_TEXT), whatever a controller sends.
  const ios = result.platform === 'ios';
  const complete = result.coverage?.complete === true && !ios;
  const gapCodes = result.coverage ? [...result.coverage.gaps] : [];
  if (ios && result.coverage && !gapCodes.includes('ios_static_sdks_not_read')) gapCodes.push('ios_static_sdks_not_read');
  const gaps = result.coverage ? gapCodes.map((g) => GAP_TEXT[g] ?? humanise(g)) : ['the server did not report what it read'];
  return (
    <div className="space-y-4" data-testid="mobile-app-findings">
      <div className={`glass-lite rounded-md p-4 border ${complete ? 'border-success/40' : 'border-warning/50'}`}>
        <div className="flex items-start gap-3">
          {complete ? (
            <CheckCircle2 className="h-5 w-5 text-success shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle className="h-5 w-5 text-warning shrink-0 mt-0.5" />
          )}
          <div className="flex-1 text-xs">
            <div className="font-medium text-sm">
              {result.platform === 'android' ? 'Android' : 'iOS'} app scanned: {result.trackers.length} tracker
              {result.trackers.length === 1 ? '' : 's'}, {result.sensitivePermissions.length} sensitive permission
              {result.sensitivePermissions.length === 1 ? '' : 's'}{' '}
              <Badge
                variant={complete ? 'success' : 'warning'}
                className="text-[10px]"
                data-testid="mobile-app-coverage"
                title={
                  complete
                    ? 'Every check listed for this scan passed. It does not mean every line of code was read: code downloaded at run time, obfuscation with long names and trackers in native code are outside any upload scan.'
                    : 'One or more checks did not pass; the reasons are listed below.'
                }
              >
                {complete ? 'complete scan' : 'scan not complete'}
              </Badge>
            </div>
            <div className="text-muted-foreground mt-1">
              App: <code className="font-mono">{result.assetExternalRef}</code>
              {result.app ? (
                <>
                  {' '}
                  (<code className="font-mono">{result.app.packageName}</code>
                  {result.app.versionCode !== null ? `, version code ${result.app.versionCode}` : ''})
                </>
              ) : null}{' '}
              · {result.accepted} finding
              {result.accepted === 1 ? '' : 's'} recorded as an operator upload under your name · signatures{' '}
              {result.signatureVersion}
            </div>
          </div>
        </div>
      </div>

      {!complete && (
        <div className="text-xs text-warning" role="status" data-testid="mobile-app-not-complete">
          Part of the app was not read: {gaps.join('; ')}. What
          was found is listed below, but a tracker missing from this list may still be in the app.
        </div>
      )}
      {!complete && (result.coverage?.runtimeEntries?.length ?? 0) > 0 && (
        <div className="text-xs text-muted-foreground" data-testid="mobile-app-runtime-entries">
          Runtime code not read, in: {result.coverage!.runtimeEntries!.map((n) => <code key={n} className="font-mono mr-2">{n}</code>)}
        </div>
      )}
      {!complete && (result.coverage?.embeddedEntries?.length ?? 0) > 0 && (
        <div className="text-xs text-muted-foreground" data-testid="mobile-app-embedded-entries">
          Embedded code or archives not read: {result.coverage!.embeddedEntries!.map((n) => <code key={n} className="font-mono mr-2">{n}</code>)}
        </div>
      )}
      {result.signer && (
        <div className="text-xs text-muted-foreground" data-testid="mobile-app-signer">
          Signed with APK signature scheme {result.signer.scheme}, certificate SHA-256{' '}
          <code className="font-mono">{result.signer.certSha256.slice(0, 16)}...</code>:{' '}
          {result.signer.verified === true
            ? 'the signature verifies against this file.'
            : result.signer.verified === false
              ? `the signature does NOT verify (${humanise(result.signer.verifyFailure ?? 'unknown')}).`
              : 'not verified.'}
        </div>
      )}
      <div className="text-xs text-muted-foreground" data-testid="mobile-app-close">
        {(result.closed ?? 0) > 0
          ? `${result.closed} finding${result.closed === 1 ? '' : 's'} from an earlier build of this app closed as no longer detected.`
          : result.closeSkipped
            ? (CLOSE_SKIPPED_TEXT[result.closeSkipped] ?? '')
            : result.closeSkipped === null
              ? 'No finding from an earlier build was closed.'
              : ''}
        {(result.keptOpen ?? 0) > 0
          ? ` ${result.keptOpen} earlier finding${result.keptOpen === 1 ? ' was' : 's were'} kept open.`
          : ''}
      </div>
      {result.keptOpenReasons && Object.keys(result.keptOpenReasons).length > 0 && (
        <ul className="text-xs text-muted-foreground list-disc ml-4" data-testid="mobile-app-kept-reasons">
          {Object.entries(result.keptOpenReasons).map(([reason, n]) => (
            <li key={reason}>
              {n} {KEPT_OPEN_TEXT[reason] ?? humanise(reason)}
            </li>
          ))}
        </ul>
      )}

      {result.trackers.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="py-1 pr-3 font-medium">Tracker</th>
                <th className="py-1 pr-3 font-medium">Vendor</th>
                <th className="py-1 pr-3 font-medium">Category</th>
                <th className="py-1 font-medium">Personal data typically collected</th>
              </tr>
            </thead>
            <tbody>
              {result.trackers.map((t) => (
                <tr key={t.id} className="border-t border-border/30 align-top">
                  <td className="py-1.5 pr-3 font-medium">{t.name}</td>
                  <td className="py-1.5 pr-3">{t.vendor}</td>
                  <td className="py-1.5 pr-3">
                    <Badge variant="glass" className="text-[10px]">
                      {humanise(t.category)}
                    </Badge>
                  </td>
                  <td className="py-1.5">{t.personalData.map(humanise).join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-xs text-muted-foreground">
          {complete
            ? 'No known tracker SDK was found in this app.'
            : 'No known tracker SDK was found in the code that was read. The scan is not complete, so this does not show the app has none.'}
        </div>
      )}

      <div className="text-xs">
        <div className="font-medium mb-1">Sensitive permissions</div>
        {result.sensitivePermissions.length > 0 ? (
          <ul className="list-disc ml-4 space-y-0.5">
            {result.sensitivePermissions.map((p) => (
              <li key={p}>
                <code className="font-mono text-[11px]">{p}</code>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-muted-foreground">
            {result.platform === 'ios' ? 'Not read for iOS apps.' : 'None declared.'}
          </div>
        )}
      </div>

      <div className="text-[10px] text-muted-foreground" data-testid="mobile-app-limits">
        {result.limits ?? ''}
      </div>

      <div className="flex flex-wrap gap-2">
        {result.accepted > 0 && (
          <Button asChild size="sm">
            <Link to="/dspm/findings" search={{ asset: result.assetExternalRef } as never}>
              View findings <ArrowRight className="h-3 w-3" />
            </Link>
          </Button>
        )}
        {onAnother && (
          <Button size="sm" variant="glass" onClick={onAnother}>
            Scan another app
          </Button>
        )}
      </div>
    </div>
  );
}
